import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Undo2, X } from 'lucide-react';
import { UndoContext, type UndoState } from '@/design/undo';

/**
 * מחיקה עם חלון חרטה, במקום שאלה לפני.
 *
 * `window.confirm` עוצר את העובד לפני כל פעולה ושואל אותו אם הוא
 * בטוח. הוא תמיד בטוח, ולכן הוא לומד ללחוץ "אישור" בלי לקרוא,
 * ושאלה שאיש אינו קורא אינה הגנה.
 *
 * כאן הפעולה פשוט קורית, והיא ניתנת לביטול לכמה שניות. היא מהירה
 * יותר ביום-יום, ומגינה טוב יותר כשבאמת טועים.
 *
 * הביטול אמיתי ולא "שחזור": הפעולה עצמה נדחית עד שהחלון נסגר, ולכן
 * ביטול פירושו שהיא לא קרתה. שחזור של שורה שנמחקה היה יוצר שורה
 * חדשה עם מזהה חדש, והילדים שלה היו נשארים מחוקים.
 */

const SECONDS = 6;

type Pending = {
  id: string;
  message: string;
  perform: () => Promise<unknown>;
  timer: number;
};

export function UndoProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending[]>([]);
  const [error, setError] = useState<string | null>(null);
  // ref ולא state: הניקוי בפריקה צריך את הרשימה העדכנית בלי להיות
  // תלוי בה, אחרת כל שינוי היה מרשם מחדש את המאזין. מסונכרן
  // באפקט, כי כתיבה ל ref בזמן רינדור אינה חוקית.
  const pendingRef = useRef<Pending[]>([]);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  const flush = useCallback((id: string) => {
    const item = pendingRef.current.find((p) => p.id === id);
    if (!item) return;
    setPending((list) => list.filter((p) => p.id !== id));
    void item.perform().catch((caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'הפעולה נכשלה.');
    });
  }, []);

  const remove = useCallback<UndoState['remove']>(
    (input) => {
      setError(null);
      const timer = window.setTimeout(() => flush(input.id), SECONDS * 1000);
      setPending((list) => [
        ...list.filter((p) => p.id !== input.id),
        { ...input, timer },
      ]);
    },
    [flush],
  );

  const cancel = useCallback((id: string) => {
    setPending((list) => {
      const item = list.find((p) => p.id === id);
      if (item) window.clearTimeout(item.timer);
      return list.filter((p) => p.id !== id);
    });
  }, []);

  // סגירת הלשונית באמצע חלון החרטה: מבצעים מיד. אחרת העובד ראה
  // "נמחק", סגר, וזה לא נמחק.
  useEffect(() => {
    const onLeave = () => {
      for (const item of pendingRef.current) {
        window.clearTimeout(item.timer);
        void item.perform();
      }
    };
    window.addEventListener('pagehide', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      onLeave();
    };
  }, []);

  // נגזר מה state: אילו קרא מה ref, ביטול לא היה מרנדר מחדש את
  // הרשימה והשורה היתה נשארת נעלמת עד לרענון
  const pendingIds = useMemo(() => new Set(pending.map((p) => p.id)), [pending]);
  const value = useMemo<UndoState>(
    () => ({ remove, isPending: (id: string) => pendingIds.has(id) }),
    [remove, pendingIds],
  );

  return (
    <UndoContext.Provider value={value}>
      {children}

      {(pending.length > 0 || error) && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-50 grid justify-items-center gap-2 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
        >
          {error && (
            <div className="flex w-full max-w-md items-center gap-3 rounded-md bg-bad p-3 text-body-sm text-on-charcoal shadow-card">
              <span className="grow">{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                aria-label="סגירה"
                className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center bg-transparent text-on-charcoal"
              >
                <X size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </div>
          )}

          {pending.map((item) => (
            <div
              key={item.id}
              className="flex w-full max-w-md items-center gap-3 rounded-md bg-charcoal p-3 text-body-sm text-on-charcoal shadow-card"
            >
              <span className="grow">{item.message}</span>
              <button
                type="button"
                onClick={() => cancel(item.id)}
                className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-sm border border-on-charcoal/40 bg-transparent px-3 font-medium text-on-charcoal hover:bg-on-charcoal/10"
              >
                <Undo2 size={16} strokeWidth={1.75} aria-hidden="true" />
                ביטול
              </button>
            </div>
          ))}
        </div>
      )}
    </UndoContext.Provider>
  );
}
